import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Briefcase, DollarSign, CheckCircle, Calendar } from 'lucide-react';
import { useTheme } from '../../contexts/Themecontext';
import { useAuth } from '../../contexts/Authcontext';
import { careersAPI } from '../../services/api';

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchJobDetails();
  }, [id]);
  const fetchJobDetails = async () => {
    setLoading(true);
    setError('');
  
    try {
      // FIX: Change getJobById to getJobByID
      const data = await careersAPI.getJobByID(id); 
      setJob(data);
    } catch (err) {
      console.error('Error fetching job details:', err);
      setError('Failed to load job details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyClick = () => {
    if (!user) {
      // Redirect to login with return URL
      navigate('/login', { state: { returnUrl: `/careers/job/${id}/apply` } });
    } else {
      navigate(`/careers/job/${id}/apply`);
    }
  };

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gray-50',
        text: 'text-gray-900',
        subtext: 'text-gray-600',
        cardBg: 'bg-white',
        cardBorder: 'border-gray-200',
        accent: 'from-blue-600 to-purple-600',
        accentText: 'text-blue-600',
        badge: 'bg-blue-100 text-blue-800',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-slate-900',
        text: 'text-white',
        subtext: 'text-gray-300',
        cardBg: 'bg-slate-800',
        cardBorder: 'border-slate-700',
        accent: 'from-cyan-600 to-blue-600',
        accentText: 'text-cyan-400',
        badge: 'bg-cyan-900 text-cyan-300',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      text: 'text-white',
      subtext: 'text-gray-200',
      cardBg: 'bg-gradient-to-br from-slate-900/50 to-purple-900/50',
      cardBorder: 'border-purple-500/30',
      accent: 'from-purple-600 to-pink-600',
      accentText: 'text-purple-400',
      badge: 'bg-purple-900/50 text-purple-300',
    };
  };

  const classes = getThemeClasses();

  if (loading) {
    return (
      <div className={`min-h-screen ${classes.bg} flex items-center justify-center pt-20`}>
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          <p className={`mt-4 ${classes.subtext}`}>Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className={`min-h-screen ${classes.bg} flex items-center justify-center pt-20`}>
        <div className="text-center max-w-md">
          <p className="text-red-500 mb-4">{error || 'Job not found'}</p>
          <Link
            to="/careers"
            className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <ArrowLeft className="mr-2 w-5 h-5" />
            Back to Careers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${classes.bg} pt-20 pb-12`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/careers"
          className={`inline-flex items-center ${classes.subtext} hover:${classes.accentText} mb-6 transition-colors`}
        >
          <ArrowLeft className="mr-2 w-5 h-5" />
          Back to all jobs
        </Link>

        {/* Job Header */}
        <div className={`${classes.cardBg} rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} mb-6`}>
          <h1 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
            {job.title}
          </h1>

          <div className="flex flex-wrap gap-4 mb-6">
            <span className={`flex items-center gap-2 ${classes.subtext}`}>
              <Briefcase className="w-5 h-5" />
              {job.department}
            </span>
            <span className={`flex items-center gap-2 ${classes.subtext}`}>
              <MapPin className="w-5 h-5" />
              {job.location}
            </span>
            <span className={`flex items-center gap-2 ${classes.subtext}`}>
              <Clock className="w-5 h-5" />
              {job.type}
            </span>
            {job.salary_range && (
              <span className={`flex items-center gap-2 ${classes.subtext}`}>
                <DollarSign className="w-5 h-5" />
                {job.salary_range}
              </span>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleApplyClick}
              className={`px-8 py-3 rounded-xl font-semibold bg-gradient-to-r ${classes.accent} text-white hover:shadow-lg transition-all`}
            >
              {user ? 'Apply Now' : 'Sign in to Apply'}
            </button>
          </div>
        </div>

        {/* Job Description */}
        <div className={`${classes.cardBg} rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} mb-6`}>
          <h2 className={`text-2xl font-bold ${classes.text} mb-4`}>About This Role</h2>
          <p className={`${classes.subtext} whitespace-pre-line`}>{job.full_description}</p>
        </div>

        {/* Requirements */}
        {job.requirements && job.requirements.length > 0 && (
          <div className={`${classes.cardBg} rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} mb-6`}>
            <h2 className={`text-2xl font-bold ${classes.text} mb-4`}>Requirements</h2>
            <ul className="space-y-3">
              {job.requirements.map((req, index) => (
                <li key={index} className={`flex items-start ${classes.subtext}`}>
                  <CheckCircle className={`w-5 h-5 ${classes.accentText} mr-3 mt-0.5 flex-shrink-0`} />
                  <span>{req}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Responsibilities */}
        {job.responsibilities && job.responsibilities.length > 0 && (
          <div className={`${classes.cardBg} rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} mb-6`}>
            <h2 className={`text-2xl font-bold ${classes.text} mb-4`}>Responsibilities</h2>
            <ul className="space-y-3">
              {job.responsibilities.map((resp, index) => (
                <li key={index} className={`flex items-start ${classes.subtext}`}>
                  <CheckCircle className={`w-5 h-5 ${classes.accentText} mr-3 mt-0.5 flex-shrink-0`} />
                  <span>{resp}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Benefits */}
        {job.benefits && job.benefits.length > 0 && (
          <div className={`${classes.cardBg} rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} mb-6`}>
            <h2 className={`text-2xl font-bold ${classes.text} mb-4`}>Benefits</h2>
            <ul className="space-y-3">
              {job.benefits.map((benefit, index) => (
                <li key={index} className={`flex items-start ${classes.subtext}`}>
                  <CheckCircle className={`w-5 h-5 ${classes.accentText} mr-3 mt-0.5 flex-shrink-0`} />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Apply CTA */}
        <div className={`${classes.cardBg} rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} text-center`}>
          <h3 className={`text-xl font-bold ${classes.text} mb-4`}>
            Ready to Join Our Team?
          </h3>
          <p className={`${classes.subtext} mb-6`}>
            Take the next step in your career and help us build the quantum future.
          </p>
          <button
            onClick={handleApplyClick}
            className={`px-8 py-3 rounded-xl font-semibold bg-gradient-to-r ${classes.accent} text-white hover:shadow-lg transition-all`}
          >
            {user ? 'Apply for This Position' : 'Sign in to Apply'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobDetails;